import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Play, Eye, Volume2, VolumeX, Lock } from 'lucide-react';
import { TwistedCrest, PentagramMark } from '../components/Crest';
import {
  MayorSeal, BiotechSeal, InstituteSeal, LegalSeal,
  TwistedMayorSeal, TwistedBiotechSeal, TwistedInstituteSeal, TwistedLegalSeal,
} from '../components/Seals';
import { MaskedLines, Reveal } from '../components/Reveal';
import { useAmbience } from '../components/Ambience';
import { API, LOCATIONS, DARK_MOTTO } from '../data/content';

const KEEPER_SEALS = {
  'Octavious León': [MayorSeal, TwistedMayorSeal],
  'Solène León': [BiotechSeal, TwistedBiotechSeal],
  'Maeve León': [InstituteSeal, TwistedInstituteSeal],
  'Henrietta León': [LegalSeal, TwistedLegalSeal],
};

const SealFlip = ({ gold: Gold, twisted: Twisted, delay = 1.3 }) => (
  <motion.div
    initial={{ rotateY: 0 }}
    animate={{ rotateY: 180 }}
    transition={{ delay, duration: 1, ease: [0.22, 1, 0.36, 1] }}
    className="relative w-12 h-12 shrink-0"
    style={{ transformStyle: 'preserve-3d' }}
    data-testid="keeper-seal"
  >
    <div className="absolute inset-0" style={{ backfaceVisibility: 'hidden' }}>
      <Gold />
    </div>
    <div className="absolute inset-0" style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
      <Twisted />
    </div>
  </motion.div>
);

export default function SecretPage() {
  const { id } = useParams();
  const fallback = LOCATIONS.find((l) => l.id === id);
  const [loc, setLoc] = useState(fallback);
  const [access, setAccess] = useState('checking');

  useEffect(() => {
    setLoc(LOCATIONS.find((l) => l.id === id));
    axios
      .get(`${API}/locations/${id}`)
      .then((r) => setLoc(r.data))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    fetch(`${API}/archive/status`, { credentials: 'include' })
      .then((r) => setAccess(r.ok ? 'unlocked' : 'locked'))
      .catch(() => setAccess('locked'));
  }, []);

  const { muted, toggleAmbience } = useAmbience(access === 'unlocked');
  const others = LOCATIONS.filter((l) => l.id !== id);

  return (
    <div className="sanctum min-h-screen bg-obsidian text-gray-200 relative overflow-hidden" data-testid="secret-page">
      <div className="grain-overlay" />
      {access === 'unlocked' && (
        <button
          type="button"
          data-testid="sanctum-audio-toggle"
          onClick={toggleAmbience}
          className="fixed bottom-6 right-6 z-[96] flex items-center gap-2 border border-occult-border bg-obsidian/80 backdrop-blur px-4 py-2.5 font-mono text-[9px] tracking-[0.3em] uppercase text-gray-400 hover:text-occult-gold hover:border-occult-gold/50 transition-colors"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-blood" />}
          {muted ? 'Ambience silenced' : 'Ambience · wind & crackle'}
        </button>
      )}
      <div className="pointer-events-none fixed -right-40 -bottom-40 w-[34rem] h-[34rem] text-crimson/15 animate-slow-rotate" aria-hidden="true">
        <PentagramMark />
      </div>
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,7,100,0.25),transparent_55%)]" aria-hidden="true" />

      {/* entry flash */}
      <motion.div
        key={id}
        className="pointer-events-none fixed inset-0 z-[95] bg-gradient-to-b from-crimson via-obsidian to-obsidian scanlines"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 1.1, ease: 'easeOut' }}
        aria-hidden="true"
      />

      <header className="relative z-10 border-b border-occult-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TwistedCrest className="w-8 h-10" />
            <div className="leading-none">
              <p className="font-serif font-semibold text-lg tracking-wide text-gray-100">THE LEÓN CONCLAVE</p>
              <p className="font-mono text-[9px] tracking-[0.3em] text-crimson mt-1">{DARK_MOTTO}</p>
            </div>
          </div>
          <Link
            to={access === 'unlocked' ? '/archive' : '/'}
            data-testid="secret-back-button"
            className="group inline-flex items-center gap-2 font-display text-xs font-semibold tracking-[0.2em] uppercase text-gray-400 hover:text-occult-gold transition-colors"
          >
            <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
            {access === 'unlocked' ? 'Return to the archive' : 'Return to the surface'}
          </Link>
        </div>
      </header>

      {access !== 'unlocked' ? (
        <main className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-32 text-center" data-testid="sanctum-gate">
          <Lock className="w-8 h-8 text-blood mx-auto mb-8" strokeWidth={1.5} />
          <p className="font-mono text-xs tracking-[0.4em] uppercase text-blood mb-6">
            {access === 'checking' ? 'Consulting the register…' : 'Entry denied'}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl text-gray-100">This record is sealed.</h1>
          <p className="mt-6 text-gray-400 max-w-md mx-auto">
            The Conclave’s chambers answer only to the Archive’s key.
          </p>
          <Link
            to="/archive"
            className="mt-10 inline-block border border-occult-border px-7 py-3 font-display text-sm tracking-widest uppercase text-occult-gold hover:border-occult-gold transition-colors"
            data-testid="gate-archive-link"
          >
            Present your pass key
          </Link>
        </main>
      ) : !loc ? (
        <main className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-32 text-center">
          <p className="font-mono text-xs tracking-[0.4em] uppercase text-blood mb-6">Entry denied</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-gray-100">This chamber is sealed.</h1>
          <p className="mt-6 text-gray-400">The Conclave keeps no record of the place you seek.</p>
          <Link to="/archive" className="mt-10 inline-block border border-occult-border px-7 py-3 font-display text-sm tracking-widest uppercase text-occult-gold hover:border-occult-gold transition-colors" data-testid="sealed-back-button">
            Return to the archive
          </Link>
        </main>
      ) : (
        <main className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 pb-28">
          <div className="pt-16 lg:pt-24">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="font-mono text-[10px] sm:text-xs tracking-[0.4em] uppercase text-blood mb-8"
              data-testid="secret-entry-meta"
            >
              Entry {loc.entry} · Sanctum Records · Clearance: Initiate
            </motion.p>
            <h1 data-testid="secret-heading" className="text-flicker font-serif font-medium text-4xl sm:text-5xl lg:text-6xl text-gray-100 leading-[1.05] tracking-tight max-w-3xl">
              <MaskedLines lines={[loc.secretTitle]} delay={0.55} />
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-[10px] tracking-[0.25em] uppercase text-gray-500"
            >
              <span>Beneath: <span className="text-occult-gold">{loc.name}</span></span>
              <span>Keeper: <span className="text-occult-gold">{loc.keeper}</span></span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="mt-7 flex items-center gap-4"
              data-testid="keeper-seal-row"
            >
              {(KEEPER_SEALS[loc.keeper] ? [KEEPER_SEALS[loc.keeper]] : Object.values(KEEPER_SEALS)).map(([Gold, Twisted], i) => (
                <SealFlip key={`${id}-${i}`} gold={Gold} twisted={Twisted} delay={1.3 + i * 0.3} />
              ))}
              <div>
                <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-gray-500">Keeper’s seal</p>
                <p className="font-serif italic text-xs text-blood/80 mt-1">gold above ground — corrupted on sight below</p>
              </div>
            </motion.div>
          </div>

          <Reveal className="mt-16 relative overflow-hidden border border-occult-border">
            <div className="aspect-[21/9] bg-occult-surface">
              <img src={loc.image} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-60 saturate-50" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-crimson/20 to-transparent" />
            <p className="absolute bottom-4 left-5 font-mono text-[10px] tracking-[0.3em] uppercase text-gray-300/80 flex items-center gap-2">
              <Eye className="w-3.5 h-3.5 text-blood" /> Witnessed sketch · Conclave archive
            </p>
          </Reveal>

          <div className="mt-16 grid lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-7 space-y-7">
              <p className="font-serif text-xl sm:text-2xl text-gray-200 leading-relaxed first-letter:text-5xl first-letter:text-blood first-letter:font-semibold first-letter:mr-2 first-letter:float-left first-letter:leading-[0.85]">
                {loc.secretLore}
              </p>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed border-l-2 border-crimson/60 pl-6">
                {loc.secretLore2}
              </p>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gray-600 pt-4">
                Transcribed by the Sentinel · Do not circulate above ground
              </p>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="border border-occult-gold/40 sanctum-glow bg-occult-surface">
                <div className="flex items-center justify-between px-5 py-3 border-b border-occult-border">
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-occult-gold flex items-center gap-2">
                    <Play className="w-3.5 h-3.5" /> Archived footage
                  </span>
                  <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-gray-600">Reel {loc.entry}</span>
                </div>
                <div className="aspect-video bg-black" data-testid="secret-video-player">
                  <iframe
                    src={`https://www.youtube.com/embed/${loc.videoId}?rel=0`}
                    title={`Archived footage — ${loc.name}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                <p className="px-5 py-3 font-mono text-[9px] tracking-[0.2em] uppercase text-gray-600">
                  Placeholder reel — swap in the true footage
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal className="mt-24">
            <div className="flex items-center gap-5 mb-8">
              <span className="h-px flex-1 bg-occult-border" />
              <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-gray-500">Other chambers below St. Bellmore</p>
              <span className="h-px flex-1 bg-occult-border" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-occult-border border border-occult-border">
              {others.map((o) => (
                <Link
                  key={o.id}
                  to={`/sanctum/${o.id}`}
                  data-testid={`secret-related-${o.id}`}
                  className="group bg-obsidian hover:bg-occult-subtle transition-colors duration-300 p-5 flex flex-col items-center text-center gap-3"
                >
                  <span className="font-mono text-[9px] tracking-[0.3em] text-blood">{o.entry}</span>
                  <span className="font-serif italic text-sm text-gray-300 group-hover:text-occult-gold transition-colors leading-snug">{o.name}</span>
                </Link>
              ))}
            </div>
          </Reveal>

          <p className="mt-20 text-center font-mono text-[9px] tracking-[0.4em] uppercase text-gray-700">
            {DARK_MOTTO} · What is seen below remains below
          </p>
        </main>
      )}
    </div>
  );
}
